import axios from 'axios';

import { handleErrors } from 'utils/api';
import { createActionType } from 'utils/redux';
import {
  apiCreateEmailImapSmtpConfig,
  apiGetEmailConfigs,
  apiGetAccountEmailConfigs,
  apiGetOrganizationEmailConfigs,
  apiUpdateOrganizationEmailConfig,
  apiOrganizationEmailConfigAddRemoveAccounts,
  apiDeleteEmailConfig,
  apiUpdateEmailImapSmtpConfig,
  apiCreateEmailKnownImapConfig,
  apiUpdateEmailKnownImapConfig,
  apiUpdateEmailOauthConfig,
  apiUpdateEmailConfig,
} from 'constants/apiRouting';
import {
  DELETE_EMAIL_CONFIG_EVENT,
  ADD_EMAIL_CONFIG_EVENT,
  REMOVE_EMAIL_CONFIG_EVENT,
  CREATE_IMAP_SMTP_CONFIG,
  UPDATE_IMAP_SMTP_CONFIG,
  CREATE_EMAIL_KNOWN_CONFIG,
  UPDATE_EMAIL_KNOWN_CONFIG,
  UPDATE_GOOGLE_OAUTH_CONFIG,
} from 'constants/analytics';
import { analyticsTrack } from '../../utils/analytics';

// object
export const GET_EMAIL_CONFIGS = createActionType('GET_EMAIL_CONFIGS');
export const CREATE_EMAIL_CONFIG = createActionType('CREATE_EMAIL_CONFIG');
export const UPDATE_EMAIL_CONFIG = createActionType('UPDATE_EMAIL_CONFIG');
export const DELETE_EMAIL_CONFIG = createActionType('DELETE_EMAIL_CONFIG');
export const GET_ORGANIZATION_EMAIL_CONFIGS = createActionType('GET_ORGANIZATION_EMAIL_CONFIGS');
export const ADD_EMAIL_CONFIG = createActionType('ADD_EMAIL_CONFIG');
export const REMOVE_EMAIL_CONFIG = createActionType('REMOVE_EMAIL_CONFIG');

export const getEmailConfigs = (params = {
  search: null,
  campaign_ids: null,
}) => async (dispatch) => {
  await dispatch({
    type: GET_EMAIL_CONFIGS.name,
    payload: axios.get(
      apiGetEmailConfigs(),
      { params, withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};


export const getAccountEmailConfigs = accountId => async (dispatch) => {
  await dispatch({
    type: GET_EMAIL_CONFIGS.name,
    payload: axios.get(
      apiGetAccountEmailConfigs(accountId),
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const deleteEmailConfig = (
  accountId,
  emailConfigId,
) => async (dispatch) => {
  analyticsTrack(DELETE_EMAIL_CONFIG_EVENT, {
    account: accountId,
    emailConfig: emailConfigId,
  }, true);
  await dispatch({
    type: DELETE_EMAIL_CONFIG.name,
    payload: axios.delete(
      apiDeleteEmailConfig(accountId, emailConfigId),
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const createEmailImapSmtpConfig = (
  accountId,
  params = {
    email: null,
    sender_name: null,
    total_number_of_emails_per_day: null,
    delay_between_each_email_sent: null,
    is_primary: false,
    imap_config: {
      username: null,
      password: null,
      host: null,
      port: null,
      is_ssl_enabled: false,
    },
    smtp_config: {
      username: null,
      password: null,
      host: null,
      port: null,
      is_ssl_enabled: false,
    },
  },
) => async (dispatch) => {
  analyticsTrack(CREATE_IMAP_SMTP_CONFIG, {
    account: accountId,
    ...params,
  }, true);
  await dispatch({
    type: CREATE_EMAIL_CONFIG.name,
    payload: axios.post(
      apiCreateEmailImapSmtpConfig(accountId),
      {
        account_id: accountId,
        ...params,
      },
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const updateEmailImapSmtpConfig = (
  accountId,
  emailConfigId,
  params = {
    email: null,
    sender_name: null,
    total_number_of_emails_per_day: null,
    delay_between_each_email_sent: null,
    is_primary: false,
    reply_to_email: null,
    imap_config: {
      username: null,
      password: null,
      host: null,
      port: null,
      is_ssl_enabled: false,
    },
  },
) => async (dispatch) => {
  analyticsTrack(UPDATE_IMAP_SMTP_CONFIG, {
    account: accountId,
    emailConfig: emailConfigId,
    ...params,
  }, true);
  await dispatch({
    type: UPDATE_EMAIL_CONFIG.name,
    payload: axios.put(
      apiUpdateEmailImapSmtpConfig(accountId, emailConfigId),
      params,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const createEmailKnownImapConfig = (
  accountId,
  params = {
    email: null,
    sender_name: null,
    password: null,
    total_number_of_emails_per_day: null,
    delay_between_each_email_sent: null,
    is_primary: false,
  },
) => async (dispatch) => {
  analyticsTrack(CREATE_EMAIL_KNOWN_CONFIG, {
    account: accountId,
    ...params,
  }, true);
  await dispatch({
    type: CREATE_EMAIL_CONFIG.name,
    payload: axios.post(
      apiCreateEmailKnownImapConfig(accountId),
      {
        account_id: accountId,
        ...params,
      },
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const updateEmailKnownConfig = (
  accountId,
  emailConfigId,
  params = {
    sender_name: null,
    password: null,
    total_number_of_emails_per_day: null,
    delay_between_each_email_sent: null,
    is_primary: false,
  },
) => async (dispatch) => {
  analyticsTrack(UPDATE_EMAIL_KNOWN_CONFIG, {
    account: accountId,
    emailConfig: emailConfigId,
    ...params,
  }, true);
  await dispatch({
    type: UPDATE_EMAIL_CONFIG.name,
    payload: axios.put(
      apiUpdateEmailKnownImapConfig(accountId, emailConfigId),
      params,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const updateEmailOauthConfig = (
  accountId,
  emailConfigId,
  params = {
    sender_name: null,
    total_number_of_emails_per_day: null,
    delay_between_each_email_sent: null,
    is_primary: false,
    signature: '',
  },
) => async (dispatch) => {
  analyticsTrack(UPDATE_GOOGLE_OAUTH_CONFIG, {
    account: accountId,
    emailConfig: emailConfigId,
    ...params,
  }, true);
  await dispatch({
    type: UPDATE_EMAIL_CONFIG.name,
    payload: axios.put(
      apiUpdateEmailOauthConfig(accountId, emailConfigId),
      params,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};

export const partialUpdateEmailConfig = (
  emailConfigId,
  params = {},
) => async (dispatch) => {
  analyticsTrack(UPDATE_EMAIL_CONFIG, {
    emailConfig: emailConfigId,
    ...params,
  }, true);
  await dispatch({
    type: UPDATE_EMAIL_CONFIG.name,
    payload: axios.patch(
      apiUpdateEmailConfig(emailConfigId),
      params,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};


export const getOrganizationEmailConfigs = organizationId => async (dispatch) => {
  await dispatch({
    type: GET_ORGANIZATION_EMAIL_CONFIGS.name,
    payload: axios.get(
      apiGetOrganizationEmailConfigs(organizationId),
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};


export const addEmailConfig = (organizationId, data) => async (dispatch) => {
  analyticsTrack(ADD_EMAIL_CONFIG_EVENT, {
    account: data.accounts,
    emailConfig: data.email_config,
  }, true);
  await dispatch({
    type: ADD_EMAIL_CONFIG.name,
    payload: axios.post(
      apiOrganizationEmailConfigAddRemoveAccounts(organizationId),
      data,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};


export const removeEmailConfig = (organizationId, data) => async (dispatch) => {
  analyticsTrack(REMOVE_EMAIL_CONFIG_EVENT, {
    account: data.accounts,
    emailConfig: data.email_config,
  }, true);
  await dispatch({
    type: REMOVE_EMAIL_CONFIG.name,
    payload: axios.delete(
      apiOrganizationEmailConfigAddRemoveAccounts(organizationId),
      { withCredentials: true, data },
    ).catch(error => handleErrors(error, dispatch)),
  });
};


export const updateEmailConfig = (organizationId, emailConfigId, data) => async (dispatch) => {
  await dispatch({
    type: UPDATE_EMAIL_CONFIG.name,
    payload: axios.put(
      apiUpdateOrganizationEmailConfig(organizationId, emailConfigId),
      data,
      { withCredentials: true },
    ).catch(error => handleErrors(error, dispatch)),
  });
};
